/**
 * Quick Mode PDV - Minimal full-screen interface for high-traffic situations.
 * Only shows: barcode input, last scanned item, running total, and pay button.
 * Press ESC to exit back to normal PDV.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { beepSuccess, beepError, beepPayment } from '@/lib/sounds';

interface QuickItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

export function QuickModePage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [barcode, setBarcode] = useState('');
  const [items, setItems] = useState<QuickItem[]>([]);
  const [lastItem, setLastItem] = useState<QuickItem | null>(null);
  const [total, setTotal] = useState(0);
  const [saleId, setSaleId] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, [items]);

  // ESC to exit, F2 to pay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        navigate('/pdv');
      }
      if (e.key === 'F2' && items.length > 0) {
        handleQuickPay();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, items]);

  // Create sale on first item
  const ensureSale = useCallback(async (): Promise<string> => {
    if (saleId) return saleId;
    const res = await apiFetch('/api/sales', { method: 'POST' });
    if (!res.ok) throw new Error('Failed to create sale');
    const data = await res.json();
    setSaleId(data.id);
    return data.id;
  }, [saleId]);

  const handleScan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!barcode.trim()) return;

    try {
      // Look up product
      const res = await apiFetch(`/api/products/barcode/${encodeURIComponent(barcode.trim())}`);
      if (!res.ok) {
        beepError();
        toast.error('Produto não encontrado');
        setBarcode('');
        return;
      }

      const product = await res.json();

      if (product.status === 'esgotado') {
        beepError();
        toast.error(`${product.name} - ESGOTADO`);
        setBarcode('');
        return;
      }

      // Ensure sale exists
      const currentSaleId = await ensureSale();

      // Add item to sale
      const addRes = await apiFetch(`/api/sales/${currentSaleId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: product.id,
          quantity: 1,
          unit_price: product.sale_price,
          unit: product.unit,
          product_name: product.name,
        }),
      });

      if (!addRes.ok) {
        beepError();
        toast.error('Erro ao adicionar item');
        setBarcode('');
        return;
      }

      beepSuccess();

      // Update local state
      const existingIdx = items.findIndex(i => i.id === product.id);
      let newItems: QuickItem[];

      if (existingIdx >= 0) {
        newItems = [...items];
        newItems[existingIdx] = {
          ...newItems[existingIdx]!,
          quantity: newItems[existingIdx]!.quantity + 1,
          subtotal: (newItems[existingIdx]!.quantity + 1) * newItems[existingIdx]!.unit_price,
        };
      } else {
        const newItem: QuickItem = {
          id: product.id,
          name: product.name,
          quantity: 1,
          unit_price: Number(product.sale_price),
          subtotal: Number(product.sale_price),
        };
        newItems = [...items, newItem];
      }

      setItems(newItems);
      setLastItem(newItems.find(i => i.id === product.id) || null);
      setTotal(newItems.reduce((sum, i) => sum + i.subtotal, 0));
    } catch {
      beepError();
      toast.error('Erro de conexão');
    }

    setBarcode('');
    inputRef.current?.focus();
  };

  const handleQuickPay = async () => {
    if (!saleId || items.length === 0 || processing) return;
    setProcessing(true);

    try {
      // Finalize with cash (most common in quick mode)
      const res = await apiFetch(`/api/sales/${saleId}/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payments: [{ method: 'dinheiro', amount: total, received_amount: total }],
        }),
      });

      if (res.ok) {
        beepPayment();
        const data = await res.json();
        toast.success(`Venda #${data.sale_number} finalizada!`);

        // Reset for next sale
        setItems([]);
        setLastItem(null);
        setTotal(0);
        setSaleId(null);
      } else {
        toast.error('Erro ao finalizar');
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setProcessing(false);
      inputRef.current?.focus();
    }
  };

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="flex h-screen flex-col bg-background select-none">
      {/* Exit hint */}
      <div className="absolute right-4 top-4 z-10">
        <button
          onClick={() => navigate('/pdv')}
          className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-3 w-3" />
          ESC para sair
        </button>
      </div>

      {/* Main content */}
      <div className="flex flex-1 flex-col items-center justify-center gap-8 p-8">
        {/* Total */}
        <motion.div
          key={total}
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="text-center"
        >
          <span className="block text-sm font-medium uppercase tracking-widest text-muted-foreground">
            TOTAL
          </span>
          <span className="block text-7xl font-bold text-primary tabular-nums">
            {formatCurrency(total)}
          </span>
          <span className="mt-2 block text-lg text-muted-foreground">
            {items.length} {items.length === 1 ? 'item' : 'itens'}
          </span>
        </motion.div>

        {/* Last scanned item */}
        <AnimatePresence mode="wait">
          {lastItem && (
            <motion.div
              key={lastItem.id + lastItem.quantity}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="rounded-xl border border-border/50 bg-card/80 px-6 py-3 text-center backdrop-blur-sm"
            >
              <span className="text-lg font-medium text-foreground">{lastItem.name}</span>
              <span className="ml-3 text-muted-foreground">
                {lastItem.quantity}x {formatCurrency(lastItem.unit_price)}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Barcode input */}
        <form onSubmit={handleScan} className="w-full max-w-md">
          <input
            ref={inputRef}
            type="text"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            placeholder="Escanear código de barras..."
            className="h-16 w-full rounded-2xl border border-border/50 bg-card/80 px-6 text-center text-xl font-medium text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 backdrop-blur-sm"
            autoComplete="off"
            autoFocus
          />
        </form>

        {/* Pay button */}
        {items.length > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={handleQuickPay}
            disabled={processing}
            className="rounded-2xl bg-gradient-to-r from-primary to-secondary px-12 py-5 text-xl font-bold text-white shadow-lg transition-all hover:scale-105 hover:shadow-[0_0_30px_hsl(160_84%_39%/0.4)] disabled:opacity-50 disabled:hover:scale-100"
          >
            {processing ? 'Processando...' : 'PAGAR -?" F2'}
          </motion.button>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <div className="flex flex-col items-center gap-2 text-muted-foreground/50">
            <ShoppingCart className="h-12 w-12" />
            <span className="text-sm">Escaneie um produto para começar</span>
          </div>
        )}
      </div>
    </div>
  );
}
